import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const NAVY = "#0B2B5C";
const RED = "#E3000F";

const relColor = (v) => (v >= 80 ? "#059669" : v >= 60 ? "#0B2B5C" : v >= 45 ? "#D97706" : "#DC2626");

const ChartTip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-border bg-white px-3 py-2 shadow-md text-xs" data-testid="exports-chart-tooltip">
      <div className="text-muted-foreground mb-1 tracking-widest uppercase text-[10px] font-mono-plex">{label}</div>
      {payload.filter((p) => p.value != null).map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="text-foreground ml-auto font-medium">{p.value}{unit}</span>
        </div>
      ))}
    </div>
  );
};

const TrendBadge = ({ trend }) => {
  const up = trend === "Increasing";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-600" : "text-red-600"}`}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {trend}
    </span>
  );
};

const Kpi = ({ k, i }) => (
  <div className="border-r border-b border-border bg-white p-6" data-testid={`exports-kpi-${i}`}>
    <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-3">{k.label}</div>
    <div className="font-display text-2xl xl:text-3xl tracking-tight mb-2 leading-tight">{k.value}</div>
    <div className="text-[11px] text-muted-foreground mb-2">{k.sub}</div>
    {k.change && (
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${k.good === true ? "text-emerald-600" : k.good === false ? "text-red-600" : "text-muted-foreground"}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${k.good === true ? "bg-emerald-500" : k.good === false ? "bg-red-500" : "bg-slate-400"}`} />
        {k.change}
      </span>
    )}
  </div>
);

const axis = { stroke: "#64748B", fontSize: 11, tickLine: false };

export default function ExportsBriefing() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [laneId, setLaneId] = useState("asia");

  useEffect(() => {
    api.get("/market/exports").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) {
    return <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="exports-error">Unable to load export-lane data. Please try again.</div>;
  }
  if (!data) {
    return <div className="border border-border bg-white p-16 text-center font-mono-plex text-xs tracking-widest uppercase text-muted-foreground animate-pulse" data-testid="exports-loading">Loading export lanes…</div>;
  }

  const lane = data.lanes.find((l) => l.id === laneId) || data.lanes[0];

  return (
    <div data-testid="exports-briefing">
      {/* Lane sub-tabs */}
      <div className="flex items-center gap-1 border-b border-border mb-8" data-testid="exports-lane-tabs">
        {data.lanes.map((l) => {
          const active = l.id === laneId;
          return (
            <button
              key={l.id}
              onClick={() => setLaneId(l.id)}
              data-testid={`exports-lane-${l.id}`}
              className={`relative px-4 py-3 text-xs font-mono-plex tracking-widest uppercase transition-colors ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {l.name}
              {active && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-[#E3000F]" />}
            </button>
          );
        })}
        <span className="ml-auto text-[11px] text-muted-foreground font-mono-plex tracking-wide hidden sm:inline">{data.meta.source}</span>
      </div>

      {/* Commentary */}
      <p className="text-sm text-muted-foreground leading-relaxed max-w-4xl mb-8">{lane.commentary}</p>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border mb-10" data-testid="exports-kpi-grid">
        {lane.kpis.map((k, i) => <Kpi key={i} k={k} i={i} />)}
      </section>

      {/* Charts */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-border bg-white p-6" data-testid="exports-reliability-chart">
          <h3 className="text-base font-semibold tracking-tight mb-1">Schedule Reliability · {lane.name}</h3>
          <p className="text-xs text-muted-foreground mb-5">On-time performance by two-month window · 2025 vs 2026 (%)</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={lane.reliability_trend} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="period" {...axis} axisLine={{ stroke: "#CBD5E1" }} angle={-35} textAnchor="end" height={60} interval={0} />
              <YAxis {...axis} axisLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<ChartTip unit="%" />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" name="2025" dataKey="y2025" stroke={NAVY} strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" name="2026" dataKey="y2026" stroke={RED} strokeWidth={2.5} dot={{ r: 3, fill: RED }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-border bg-white p-6" data-testid="exports-delay-chart">
          <h3 className="text-base font-semibold tracking-tight mb-1">Avg Delay · LATE Vessels · {lane.name}</h3>
          <p className="text-xs text-muted-foreground mb-5">Average delay for late arrivals · 2025 vs 2026 (days)</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={lane.delay_trend} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="period" {...axis} axisLine={{ stroke: "#CBD5E1" }} angle={-35} textAnchor="end" height={60} interval={0} />
              <YAxis {...axis} axisLine={false} tickFormatter={(v) => `${v}d`} />
              <Tooltip content={<ChartTip unit=" days" />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" name="2025" dataKey="y2025" stroke={NAVY} strokeWidth={2} dot={false} connectNulls />
              <Line type="monotone" name="2026" dataKey="y2026" stroke={RED} strokeWidth={2.5} dot={{ r: 3, fill: RED }} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Carrier performance */}
      <section className="mb-10" data-testid="exports-carriers">
        <h3 className="text-base font-semibold tracking-tight mb-1">Carrier Performance · {lane.name}</h3>
        <p className="text-xs text-muted-foreground mb-5">Apr/May 2026 on-time reliability, 6-month trend &amp; tracked arrivals</p>
        <div className="border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Carrier</th>
                <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold w-[40%]">Apr/May On-Time</th>
                <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">6M Trend</th>
                <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold text-right">Arrivals</th>
              </tr>
            </thead>
            <tbody>
              {lane.carriers.map((c, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`exports-carrier-${i}`}>
                  <td className="px-4 py-2.5 font-medium">{c.carrier}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <div className="h-2 flex-1 max-w-[180px] bg-slate-100 overflow-hidden rounded-sm">
                        <div className="h-full rounded-sm" style={{ width: `${c.reliability}%`, background: relColor(c.reliability) }} />
                      </div>
                      <span className="tabular-nums font-semibold" style={{ color: relColor(c.reliability) }}>{c.reliability.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><TrendBadge trend={c.trend} /></td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{c.arrivals}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Service specifics */}
      <section data-testid="exports-services">
        <h3 className="text-base font-semibold tracking-tight mb-1">Service Specifics · {lane.name}</h3>
        <p className="text-xs text-muted-foreground mb-5">On-time performance by service · Apr 2026 – May 2026</p>
        <div className="border border-border overflow-hidden">
          <div className="max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Carriers</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold">Service</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold text-right">Arrivals</th>
                  <th className="px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold text-right">% On-Time</th>
                </tr>
              </thead>
              <tbody>
                {lane.services.map((s, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`exports-service-${i}`}>
                    <td className="px-4 py-2.5 text-muted-foreground align-top">{s.carriers}</td>
                    <td className="px-4 py-2.5 font-medium align-top">{s.service}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground align-top">{s.arrivals}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold align-top" style={{ color: relColor(s.on_time) }}>{s.on_time.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
